<template>
  <div
    ref="customSelectContainer"
    class="custom-select-container tw-inline-block tw-w-full tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer tw-select-none tw-outline-none"
    tabindex="0"
    @keydown.esc="isOpen = false"
    @keydown.enter.prevent="handleEnterKey"
    @keydown.down.prevent="navigateOptions('down')"
    @keydown.up.prevent="navigateOptions('up')"
  >
    <div
      class="selected-option-display tw-px-4 tw-py-3 tw-flex tw-justify-between tw-items-center"
      role="button"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="listbox"
      @click="toggleDropdown"
    >
      <span class="selected-text tw-text-base tw-text-gray-800">{{ currentSelectionText }}</span>
      <svg
        class="chevron-icon tw-w-6 tw-h-6 tw-fill-current tw-text-gray-600 tw-transition-transform tw-duration-200 tw-ease-in-out"
        :class="{ 'tw-rotate-180': isOpen }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
      </svg>
    </div>

    <ul
      v-if="isOpen"
      class="options-list tw-list-none tw-p-0 tw-m-0 tw-border-t tw-border-gray-200"
      role="listbox"
      :aria-activedescendant="focusedOptionId"
    >
      <li
        v-for="(option, index) in options"
        :id="`option-${uniqueId}-${index}`"
        :key="option.value"
        class="option-item tw-px-4 tw-py-3 tw-text-base tw-text-gray-800 tw-border-b tw-border-gray-200 last:tw-border-b-0 tw-transition-colors tw-duration-100 tw-ease-in-out hover:tw-bg-gray-100"
        :class="{
          'tw-font-medium': option.value === internalValue,
          'tw-bg-gray-100': index === focusedOptionIndex,
          'tw-border-b tw-rounded-[8px]': index === options?.length - 1,
        }"
        role="option"
        :aria-selected="option.value === internalValue"
        @click="selectOption(option)"
        @mouseenter="focusedOptionIndex = index"
      >
        {{ option.text }}
      </li>
    </ul>
  </div>
</template>

<script>
let G_UNIQUE_ID = 0;

export default {
  name: 'CustomSelect',
  props: {
    options: {
      type: Array,
      required: true,
      validator: opts =>
        opts.every(
          opt => typeof opt.text !== 'undefined' && typeof opt.value !== 'undefined'
        ),
    },
    value: {
      // For v-model
      type: [String, Number, Object],
      default: null,
    },
    placeholder: {
      // Placeholder for when no option is selected (used if internalValue is null and no auto-select)
      type: String,
      default: 'Select an option',
    },
  },
  data() {
    return {
      isOpen: false,
      internalValue: this.value,
      focusedOptionIndex: -1,
      uniqueId: G_UNIQUE_ID++
    };
  },
  computed: {
    currentSelectionText() {
      const selectedOption = this.options.find(
        opt => opt.value === this.internalValue
      );
      if (selectedOption) {
        return selectedOption.text;
      }
      // If internalValue is null (e.g. options are empty, or initial value was null and options empty)
      // and a placeholder is provided, show placeholder.
      // The created() hook usually ensures internalValue is set if options exist.
      return this.placeholder;
    },
    focusedOptionId() {
      if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.options.length) {
        return `option-${this.uniqueId}-${this.focusedOptionIndex}`;
      }
      return null;
    }
  },
  watch: {
    value(newValue) {
      this.internalValue = newValue;
      // Update focused index if value changes externally while open
      if (this.isOpen) {
        this.focusedOptionIndex = this.options.findIndex(opt => opt.value === newValue);
      }
    },
    isOpen(open) {
      if (open) {
        // When opening, set focus to the currently selected item or the first item
        this.focusedOptionIndex = this.options.findIndex(opt => opt.value === this.internalValue);
        if (this.focusedOptionIndex === -1 && this.options.length > 0) {
          this.focusedOptionIndex = 0; // Focus first if no selection or current selection not found
        }
        // Scroll the focused item into view
        this.$nextTick(() => {
          // The .tw-bg-gray-100 class is applied to the focused item
          const focusedEl = this.$el.querySelector('li.option-item.tw-bg-gray-100');
          if (focusedEl) {
            focusedEl.scrollIntoView({ block: 'nearest' });
          }
        });
      } else {
        this.focusedOptionIndex = -1; // Reset focus index when closing
      }
    }
  },
  created() {
    // If no initial value is provided via v-model/prop, but options exist,
    // set internalValue to the first option's value to match the desired "closed" state.
    if (this.internalValue === null && this.options.length > 0) {
      this.internalValue = this.options[0].value;
      // Optionally emit this default if you want v-model to pick it up immediately
      // this.$emit('input', this.options[0].value);
    }
  },
  mounted() {
    document.addEventListener('click', this.closeOnClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeOnClickOutside);
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        // Return focus to the main select element when closing via click
        this.$refs.customSelectContainer.focus();
        this.$emit('open', false)
      } else {
        this.$emit('open', true)
      }
    },
    selectOption(option) {
      this.internalValue = option.value;
      this.$emit('input', option.value); // For v-model
      this.$emit('change', option.value); // For @change event
      this.isOpen = false;
      this.$refs.customSelectContainer.focus(); // Return focus to main element
    },
    closeOnClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
    handleEnterKey() {
      if (this.isOpen) {
        if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.options.length) {
          this.selectOption(this.options[this.focusedOptionIndex]);
        } else {
          this.isOpen = false; // Close if no option is focused (e.g. header was somehow focused)
        }
      } else {
        this.isOpen = true; // Open if closed
      }
    },
    navigateOptions(direction) {
      if (!this.isOpen) {
        this.isOpen = true; // Open if closed and navigating
        return;
      }
      const optionsCount = this.options.length;
      if (optionsCount === 0) return;

      if (direction === 'down') {
        this.focusedOptionIndex = (this.focusedOptionIndex + 1) % optionsCount;
      } else if (direction === 'up') {
        this.focusedOptionIndex = (this.focusedOptionIndex - 1 + optionsCount) % optionsCount;
      }
      // Scroll the newly focused item into view
      this.$nextTick(() => {
        const focusedEl = this.$el.querySelector('li.option-item.tw-bg-gray-100');
        if (focusedEl) {
          focusedEl.scrollIntoView({ block: 'nearest' });
        }
      });
    }
  },
};
</script>
